import React, { useCallback, useEffect } from "react";
import LoginEmailLogo from "../../Assets/ClientPortalLoginPage/LoginEmailLogo.svg";
import CheckMarkLottie from "../../Assets/ClientPortalLoginPage/CheckMarkLottie.json";
import { ReactComponent as ArrowRightIcon } from "../../Assets/ActionIcons/ArrowRightIcon.svg";
import ZomentumLogo from "../../Assets/PaymentsAppHeader/ZomentumLogo.svg";
import { useClientPortalMetadataStore } from "../../Store/ClientPortalMetadata";
import { useNavigate, useParams } from "react-router-dom";
import Lottie from "react-lottie";
import { PaymentRoutes } from "../../Contracts/Routes";
import ClientPortalBrandingNameLogo from "../../Components/Molecules/ClientPortalPages/ClientPortalBrandingNameLogo";
import { validateEmail } from "../../Contracts/Common";
import MessageBanner from "../../Components/Molecules/PaymentsAppHeader/MessageBanner";

const ClientPortalLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [emailValid, setEmailValid] = React.useState(true);
  const [emailSent, setEmailSent] = React.useState(false);
  const {
    isLoading,
    getClientPortalMetadata,
    postLoginClientPortal,
    partnerWhiteLabelMetadata,
    setClientPortalTokenAndUUID
  } = useClientPortalMetadataStore();
  const { id: uuid } = useParams();
  const refreshThingsForLoginPage = useCallback(() => {
    if (uuid) {
      getClientPortalMetadata(uuid);
    }
  }, [getClientPortalMetadata, uuid]);

  const onVerifyEmailClick = useCallback(async () => {
    if (!validateEmail(email)) {
      setEmailValid(false);
      return;
    }
    setEmailValid(true);
    if (uuid) {
      await postLoginClientPortal(uuid, email);
      setEmailSent(true);
    }
  }, [postLoginClientPortal, uuid, email]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    if (token && uuid) {
      setClientPortalTokenAndUUID(token, uuid);
      navigate(PaymentRoutes.CLIENT_PORTAL_INVOICES);
    } else {
      refreshThingsForLoginPage();
    }
  }, [uuid, refreshThingsForLoginPage, navigate, setClientPortalTokenAndUUID]);

  return (
    <>
      <MessageBanner />
      {!emailSent ? (
        <div className="flex items-center justify-center h-screen">
          <ClientPortalBrandingNameLogo className="absolute top-0 left-0 m-4" />
          <div className="absolute right-0 top-0 flex items-center space-x-2 m-4 opacity-60">
            <span className="mt-0.5">Powered by </span>
            <img src={ZomentumLogo} alt="Zomentum Logo" className="w-28" />
          </div>
          <div className="flex flex-col items-center mb-48">
            <img src={LoginEmailLogo} alt="LoginEmailLogo" className="mb-12" />
            <div className="text-3xl mb-2">Please Enter Your Email</div>
            <div className="text-gray-700 mb-2">
              You will receive a link to access the client portal
            </div>
            <input
              type="email"
              placeholder="Enter your email address"
              className={`border border-gray-300 rounded-md p-2 mt-4 w-80 ${
                isLoading ? "cursor-progress" : ""
              }`}
              autoFocus
              value={email}
              disabled={isLoading && email.length > 0}
              onChange={e => setEmail(e.target.value)}
            />
            {!emailValid && (
              <div className="mt-1 italic text-red-600">Please enter a valid email</div>
            )}
            <button
              className={`group flex items-center justify-center bg-blue-500 text-white rounded-md p-2 mt-4 w-80 hover:bg-blue-600 hover:shadow-lg duration-200 transition-all ${
                isLoading ? "opacity-50 cursor-progress" : ""
              }`}
              onClick={onVerifyEmailClick}
              disabled={isLoading}
            >
              Verify Email
              <ArrowRightIcon className="inline-block ml-2 transform group-hover:translate-x-2 duration-300 transition-all text-white" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
          <div className="flex items-center absolute left-0 top-0 m-4 space-x-2">
            {partnerWhiteLabelMetadata?.branding_company_logo_s3_url &&
              partnerWhiteLabelMetadata.branding_company_logo_s3_url.length > 0 && (
                <img
                  src={partnerWhiteLabelMetadata.branding_company_logo_s3_url}
                  alt="Company Logo"
                  className="max-h-10"
                />
              )}
            <span className="font-semibold">{partnerWhiteLabelMetadata?.name}</span>
          </div>
          <div className="flex flex-col items-center space-y-4 bg-white px-44 py-12">
            <Lottie
              options={{ animationData: CheckMarkLottie }}
              width="14rem"
              isClickToPauseDisabled
              style={{ cursor: "default" }}
            />
            <div className="text-lime-600">Email Sent!</div>
            <div className="max-w-80 text-center">
              Your login link has been sent to your registered email id:{" "}
              <span className="font-semibold">{email}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2 m-4 opacity-60">
            <span className="mt-0.5">Powered by </span>
            <img src={ZomentumLogo} alt="Zomentum Logo" className="w-28" />
          </div>
        </div>
      )}
    </>
  );
};

export default ClientPortalLoginPage;
